/**
 * The app keeps the sign-in and settings in localStorage. When the browser blocks site data for the
 * page (e.g. "block all cookies"), reading it throws and the app cannot start, so main.ts checks first
 * and shows this message instead of a blank page. Plain DOM: it runs before Angular, so it copies the
 * 404 page's layout (auth layout) in src/styles/storage-blocked.scss, with its own photo.
 */
export function storageBlocked(): boolean {
  try {
    const storage = window.localStorage;
    const key = '__g_storage_check';
    storage.setItem(key, '1');
    storage.removeItem(key);
    return false;
  } catch {
    return true;
  }
}

const TEXTS = {
  pl: {
    title: 'Twoja przeglądarka blokuje zapisywanie danych dla tej strony',
    body: 'Groomoteka potrzebuje tego, żeby utrzymać logowanie i zapamiętać ustawienia. Zezwól na dane witryny dla {host} (ikona kłódki obok adresu → Ustawienia witryny) i odśwież stronę.',
    reload: 'Odśwież',
    tagline: 'Wizyty, klienci i ich pupile — Twój salon w jednym miejscu.',
    aiPhotos: 'Zdjęcie wygenerowane przez AI',
  },
  en: {
    title: 'Your browser blocks saving data for this site',
    body: 'Groomoteka needs it to keep you signed in and remember your settings. Allow site data for {host} (the padlock next to the address → Site settings) and reload the page.',
    reload: 'Reload',
    tagline: 'Visits, clients and their pets — your salon in one place.',
    aiPhotos: 'Photo generated with AI',
  },
};

// The logo mark (shared/ui/logo) and its own photo (assets/images/access-*.webp); no text from outside goes into this markup.
const LAYOUT = `
  <aside class="g-sb-photo">
    <img src="assets/images/access-800.webp" srcset="assets/images/access-800.webp 800w, assets/images/access-2000.webp 2000w"
      sizes="(max-width: 991.98px) 100vw, 50vw" alt="" />
    <span class="g-sb-logo">
      <svg viewBox="0 0 80 80" width="36" height="36" aria-hidden="true">
        <path d="M27 50 L36 36 Q47 20 66 8 Q55 24 45 38 L35 53 Z" fill="currentColor" />
        <path d="M53 50 L44 36 Q33 20 14 8 Q25 24 35 38 L45 53 Z" fill="currentColor" />
        <circle cx="24" cy="58" r="11" fill="none" stroke="currentColor" stroke-width="7" />
        <circle cx="56" cy="58" r="11" fill="none" stroke="currentColor" stroke-width="7" />
        <circle cx="24" cy="59" r="3.6" fill="currentColor" />
        <circle cx="56" cy="59" r="3.6" fill="currentColor" />
        <path class="nose" d="M34.5 70 Q40 66 45.5 70 Q44 76 40 77 Q36 76 34.5 70 Z" />
      </svg>
      <span class="word" aria-label="Groomoteka">gr<span>oo</span>moteka</span>
    </span>
    <p class="g-sb-tagline"></p>
  </aside>
  <main class="g-sb-side">
    <div class="g-sb-card" data-testid="storage-blocked">
      <h1></h1>
      <p class="lead"></p>
      <button type="button"></button>
    </div>
    <p class="g-sb-ai"></p>
  </main>`;

export function showStorageBlocked(doc: Document, reload: () => void = () => doc.defaultView?.location.reload()): void {
  const lang = (doc.defaultView?.navigator.language ?? 'pl').toLowerCase().startsWith('pl') ? 'pl' : 'en';
  const text = TEXTS[lang];
  doc.documentElement.lang = lang;
  // No ThemeService yet: follow the system theme.
  doc.documentElement.style.colorScheme = 'light dark';

  const root = doc.createElement('div');
  root.className = 'g-storage-blocked';
  root.innerHTML = LAYOUT;
  const set = (selector: string, value: string) => (root.querySelector(selector)!.textContent = value);
  set('.g-sb-tagline', text.tagline);
  set('h1', text.title);
  set('.lead', text.body.replace('{host}', doc.location.host));
  set('button', text.reload);
  set('.g-sb-ai', text.aiPhotos);
  root.querySelector('button')!.addEventListener('click', () => reload());
  doc.body.replaceChildren(root);
}
